import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise } from 'shared/icons';

import PageHeader from '../components/layout/PageHeader';
import ProductAnalysisTable from '../components/sellout/ProductAnalysisTable';
import SelloutDashboard from '../components/sellout/SelloutDashboard';
import Button from '../components/ui/Button';
import DateRangeFilter from '../components/ui/DateRangeFilter';
import ErrorCard from '../components/ui/ErrorCard';
import TableShell from '../components/ui/TableShell';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import { useDataCache } from '../hooks/useDataCache';
import { usePurchaseCart } from '../hooks/usePurchaseCart';
import { api } from '../services/api';
import { CreateLogger } from '../services/logger';
import presetManager from '../services/PresetManager';
// import api from '../services/api';
import { BrazilDateToUTCEnd, BrazilDateToUTCStart, FormatBrazilPeriod } from '../utils/dateUtils';
import ProductDataProcessor from '../utils/ProductDataProcessor';

const logger = CreateLogger('pages/sellout');

const DEFAULT_SELLOUT_DAYS = 15;

const computeDefaultRange = () => {
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - DEFAULT_SELLOUT_DAYS);
    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    return { from: fmt(past), to: fmt(today) };
};

const Sellout = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const { orders: cachedOrders, ordersRange, ordersError, fetchOrdersAndStock } = useDataCache();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState({ from: '', to: '' });
    const [dateRange, setDateRange] = useState(computeDefaultRange); // Guardar datas originais YYYY-MM-DD
    const [processingError, setProcessingError] = useState(null);
    const [activePreset, setActivePreset] = useState(null);
    const [generatingPurchaseOrders, setGeneratingPurchaseOrders] = useState(false);
    const hasLoadedInitial = useRef(false);
    const canManagePurchaseOrders = checkPermission('purchasing:create');
    const {
        items: purchaseCart,
        summary: cartSummary,
        addItem: addCartItem,
        updateItemQuantity: updateCartQuantity,
        removeItem: removeCartItem,
        clear: clearCart,
        payloadItems,
    } = usePurchaseCart();

    // Filtrar orders pelo periodo atual (nao usar todo o cache)
    const filteredOrders = useMemo(() => {
        if (!orders || orders.length === 0 || !period.from || !period.to) {
            return [];
        }

        const periodStart = new Date(period.from);
        const periodEnd = new Date(period.to);

        return orders.filter((order) => {
            if (!order.date_created) {
                return false;
            }
            const orderDate = new Date(order.date_created);
            return orderDate >= periodStart && orderDate <= periodEnd;
        });
    }, [orders, period]);

    // Processar orders filtrados e calcular metricas por produto
    const processedProducts = useMemo(() => {
        if (!filteredOrders || filteredOrders.length === 0) {
            return [];
        }

        try {
            setProcessingError(null);
            const processor = new ProductDataProcessor(filteredOrders, period, activePreset);
            const products = processor.processProducts();
            return products;
        } catch (err) {
            setProcessingError(err.message);
            return [];
        }
    }, [filteredOrders, period, activePreset]);

    // Inicializar preset manager e carregar preset ativo
    useEffect(() => {
        const initPresets = async () => {
            try {
                await presetManager.initialize();
                setActivePreset(presetManager.getActivePreset());
            } catch (error) {
                logger.error('preset_manager_init_failed', {
                    error: {
                        name: error?.name,
                        message: error?.message,
                    },
                });
            }
        };

        initPresets();

        // Listener para mudancas de preset
        const handlePresetChange = () => {
            setActivePreset(presetManager.getActivePreset());
        };

        presetManager.addListener(handlePresetChange);

        return () => {
            presetManager.removeListener(handlePresetChange);
        };
    }, []);

    const coversRange = useCallback((cached, requested) => {
        if (!cached?.from || !cached?.to || !requested?.from || !requested?.to) {
            return false;
        }
        const cachedFrom = new Date(cached.from);
        const cachedTo = new Date(cached.to);
        const reqFrom = new Date(requested.from);
        const reqTo = new Date(requested.to);
        return reqFrom >= cachedFrom && reqTo <= cachedTo;
    }, []);

    const loadOrders = useCallback(
        async (dateFrom, dateTo, force = false) => {
            try {
                setLoading(true);
                setError(null);

                const fromUTC = BrazilDateToUTCStart(dateFrom);
                const toUTC = BrazilDateToUTCEnd(dateTo);

                const response = await fetchOrdersAndStock({
                    from: fromUTC,
                    to: toUTC,
                    force,
                });

                const newOrders = response?.orders || cachedOrders || [];
                setOrders(newOrders);
                setPeriod({ from: fromUTC, to: toUTC });
                setDateRange({ from: dateFrom, to: dateTo });
            } catch (err) {
                const errorMessage = err.message || err.response?.data?.message || 'Erro desconhecido';

                if (
                    errorMessage.includes('Token de acesso expirado') ||
                    errorMessage.includes('token_expired') ||
                    errorMessage.includes('REAUTH_REQUIRED')
                ) {
                    setError({
                        title: 'Token Expirado',
                        message:
                            'O token de acesso do MercadoLibre expirou (validade de 6 horas).\n\nEntre em contato com o administrador para renovar o token usando o REFRESH_TOKEN.',
                        action: null,
                        type: 'warning',
                    });
                } else if (errorMessage.includes('Sem permissao') || errorMessage.includes('403')) {
                    setError({
                        title: 'Sem Permissao',
                        message:
                            'A aplicacao nao tem permissao para acessar pedidos.\n\nVerifique se os escopos necessarios (read, write) estao configurados na aplicacao MercadoLibre.',
                        action: null,
                        type: 'error',
                    });
                } else if (errorMessage.includes('INVALID_PERIOD')) {
                    setError({
                        title: 'Periodo Invalido',
                        message: errorMessage,
                        action: null,
                        type: 'error',
                    });
                } else if (errorMessage.includes('MISSING_PARAMETERS')) {
                    setError({
                        title: 'Parametros Invalidos',
                        message: errorMessage,
                        action: null,
                        type: 'error',
                    });
                } else if (errorMessage.includes('nao configurado') || errorMessage.includes('nao encontrado')) {
                    setError({
                        title: 'Configuracao Pendente',
                        message: `${errorMessage}\n\nConfigure o arquivo ml_config_essentials.py com suas credenciais do MercadoLibre.`,
                        action: null,
                        type: 'info',
                    });
                } else {
                    setError({
                        title: 'Erro ao Carregar Pedidos',
                        message: errorMessage,
                        action: 'Tentar Novamente',
                        actionFn: () => loadOrders(dateFrom, dateTo, force),
                        type: 'error',
                    });
                }
            } finally {
                setLoading(false);
            }
        },
        [cachedOrders, fetchOrdersAndStock],
    );

    const handleAddToCart = useCallback(
        (item) => {
            const added = addCartItem(item);
            if (!added) {
                toast.error('Nao foi possivel adicionar o item ao carrinho');
                return;
            }

            toast.success('Produto adicionado ao carrinho');
        },
        [addCartItem, toast],
    );

    const handleGeneratePurchaseOrders = useCallback(async () => {
        if (purchaseCart.length === 0) {
            toast.error('Adicione ao menos um produto no carrinho');
            return;
        }

        setGeneratingPurchaseOrders(true);
        try {
            const response = await api.generatePurchaseOrders({
                items: payloadItems,
            });

            const createdCount = Number(response?.total || response?.results?.length || 0);
            toast.success(`${createdCount} OC(s) gerada(s) com sucesso`);
            clearCart();
        } catch (error) {
            toast.error(error.message || 'Erro ao gerar ordens de compra');
        } finally {
            setGeneratingPurchaseOrders(false);
        }
    }, [clearCart, payloadItems, purchaseCart.length, toast]);

    // Carregar orders ao montar o componente (usa dateRange inicial computado no useState)
    useEffect(() => {
        if (hasLoadedInitial.current) {
            return;
        }

        const { from: defaultFrom, to: defaultTo } = dateRange;
        if (!defaultFrom || !defaultTo) {
            return;
        }

        const defaultRangeUtc = {
            from: BrazilDateToUTCStart(defaultFrom),
            to: BrazilDateToUTCEnd(defaultTo),
        };

        if (ordersRange && cachedOrders?.length) {
            const coversDefault =
                new Date(defaultRangeUtc.from) >= new Date(ordersRange.from || '') &&
                new Date(defaultRangeUtc.to) <= new Date(ordersRange.to || '');

            if (coversDefault) {
                setOrders(cachedOrders);
                setPeriod(defaultRangeUtc);
                hasLoadedInitial.current = true;
                return;
            }
        }

        hasLoadedInitial.current = true;
        loadOrders(defaultFrom, defaultTo);
    }, [cachedOrders, dateRange, loadOrders, ordersRange]);

    useEffect(() => {
        if (!ordersRange || !cachedOrders?.length || !period.from || !period.to) {
            return;
        }
        if (coversRange(ordersRange, period)) {
            setOrders(cachedOrders);
        }
    }, [cachedOrders, coversRange, ordersRange, period]);

    useEffect(() => {
        if (!ordersError) {
            return;
        }
        setError({
            title: 'Erro ao Carregar Pedidos',
            message: ordersError,
            type: 'error',
        });
    }, [ordersError]);

    if (error) {
        return (
            <div className="space-y-6">
                <PageHeader title="Sellout" subtitle="Análise consolidada de pedidos e desempenho por período" />
                <DateRangeFilter value={dateRange} onApply={loadOrders} />
                <ErrorCard
                    title={error.title}
                    message={error.message}
                    action={error.action}
                    actionFn={error.actionFn}
                    actionLink={error.actionLink}
                    type={error.type}
                />
            </div>
        );
    }

    // Erro no processamento
    if (processingError) {
        return (
            <div className="space-y-6">
                <PageHeader
                    title="Sellout"
                    subtitle="Análise consolidada de pedidos e desempenho por período"
                    actions={
                        <Button onClick={() => loadOrders(dateRange.from, dateRange.to)} size="sm">
                            <ArrowClockwise size={18} className="mr-2" />
                            Atualizar
                        </Button>
                    }
                />
                <DateRangeFilter value={dateRange} onApply={loadOrders} />
                <ErrorCard
                    title="Erro no Processamento"
                    message={processingError}
                    action="Tentar Novamente"
                    actionFn={() => loadOrders(dateRange.from, dateRange.to)}
                    type="error"
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Sellout"
                subtitle="Análise consolidada de pedidos e desempenho por período"
                actions={
                    <Button onClick={() => loadOrders(dateRange.from, dateRange.to)} size="sm" disabled={loading}>
                        <ArrowClockwise size={18} className="mr-2" />
                        Atualizar
                    </Button>
                }
            />

            <DateRangeFilter onApply={loadOrders} defaultDays={15} />

            {loading ? (
                <>
                    {/* Skeleton: Card de resumo de pedidos */}
                    <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-4">
                        <div className="h-5 w-64 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                    </div>

                    {/* Skeleton: Dashboard de Analise */}
                    <div className="space-y-6">
                        {/* Header do Dashboard */}
                        <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                            <div className="h-6 w-48 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mb-4" />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[...Array(3)].map((_, idx) => (
                                    <div key={idx}>
                                        <div className="h-4 w-24 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mb-2" />
                                        <div className="h-8 w-32 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Grid de Graficos */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {[...Array(6)].map((_, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6"
                                >
                                    <div className="h-5 w-48 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mb-4" />
                                    <div className="h-[300px] bg-mono-100 dark:bg-mono-900 rounded animate-pulse" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Skeleton: Tabela de Produtos */}
                    <TableShell tableWrapClassName="">
                        {/* Header da tabela */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-mono-200 dark:border-mono-800">
                            <div className="h-5 w-48 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                            <div className="h-9 w-36 bg-mono-200 dark:bg-mono-800 rounded-lg animate-pulse" />
                        </div>

                        {/* Tabela */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-mono-50 dark:bg-mono-950 border-b border-mono-200 dark:border-mono-800">
                                    <tr>
                                        <th className="px-3 py-3 text-left w-[250px]">
                                            <div className="h-4 w-16 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                                        </th>
                                        {[...Array(10)].map((_, idx) => (
                                            <th key={idx} className="px-3 py-3 text-center w-20">
                                                <div className="h-4 w-12 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mx-auto" />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                                    {[...Array(5)].map((_, rowIdx) => (
                                        <tr key={rowIdx}>
                                            <td className="px-3 py-3">
                                                <div className="space-y-1">
                                                    <div className="h-4 w-full bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                                                    <div className="h-4 w-3/4 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                                                </div>
                                            </td>
                                            {[...Array(10)].map((_, colIdx) => (
                                                <td key={colIdx} className="px-3 py-3 text-center">
                                                    <div className="h-4 w-8 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mx-auto" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer com paginacao */}
                        <div className="flex items-center justify-between px-4 py-3 border-t border-mono-200 dark:border-mono-800">
                            <div className="h-5 w-32 bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                            <div className="flex gap-2">
                                {[...Array(3)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="h-8 w-8 bg-mono-200 dark:bg-mono-800 rounded animate-pulse"
                                    />
                                ))}
                            </div>
                        </div>
                    </TableShell>

                    {/* Mensagem de carregamento centralizada */}
                    <div className="fixed bottom-8 right-8 bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-4 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black dark:border-white" />
                            <p className="text-sm text-mono-600 dark:text-mono-400">Processando dados do periodo...</p>
                        </div>
                    </div>
                </>
            ) : filteredOrders.length === 0 ? (
                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                    <p className="text-mono-600 dark:text-mono-400">Nenhum pedido encontrado no periodo selecionado</p>
                    {period.from && period.to && (
                        <p className="text-sm text-mono-500 dark:text-mono-500 mt-2">
                            Periodo: {FormatBrazilPeriod(period.from, period.to)}
                        </p>
                    )}
                </div>
            ) : (
                <>
                    <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-4">
                        <p className="text-sm text-mono-700 dark:text-mono-300">
                            <span className="font-semibold text-black dark:text-white">{filteredOrders.length}</span>{' '}
                            pedidos encontrados
                            {period.from && period.to && (
                                <span className="text-mono-500 dark:text-mono-500 ml-2">
                                    ({FormatBrazilPeriod(period.from, period.to)})
                                </span>
                            )}
                        </p>
                    </div>

                    {canManagePurchaseOrders ? (
                        <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-4 space-y-4">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <h3 className="text-sm font-semibold text-black dark:text-white">
                                        Carrinho de compra
                                    </h3>
                                    <p className="text-xs text-mono-600 dark:text-mono-400">
                                        {cartSummary.totalItems} SKU(s) | {cartSummary.totalUnits} unidade(s) | Custo
                                        estimado:{' '}
                                        {Number(cartSummary.estimatedCost || 0).toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                        })}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={clearCart}
                                        disabled={purchaseCart.length === 0 || generatingPurchaseOrders}
                                    >
                                        Limpar
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleGeneratePurchaseOrders}
                                        disabled={purchaseCart.length === 0 || generatingPurchaseOrders}
                                    >
                                        {generatingPurchaseOrders ? 'Gerando...' : 'Gerar OC'}
                                    </Button>
                                </div>
                            </div>

                            {purchaseCart.length === 0 ? (
                                <p className="text-sm text-mono-500 dark:text-mono-500">
                                    Use o icone de carrinho na primeira coluna da tabela para adicionar itens.
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[900px]">
                                        <thead className="border-b border-mono-200 dark:border-mono-800">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    SKU
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    Produto
                                                </th>
                                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    MLB
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    Estoque (Bling)
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    Preco custo
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    Quantidade
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-mono-500 dark:text-mono-400">
                                                    Acoes
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                                            {purchaseCart.map((item) => (
                                                <tr key={item.key}>
                                                    <td className="px-3 py-2 text-xs font-mono text-mono-700 dark:text-mono-300">
                                                        {item.sku || '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-black dark:text-white">
                                                        {item.product_name || item.title || '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs font-mono text-mono-700 dark:text-mono-300">
                                                        {Array.isArray(item.item_ids) && item.item_ids.length > 0
                                                            ? item.item_ids.join(', ')
                                                            : item.item_id || '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-sm text-black dark:text-white">
                                                        {Number(item.bling_stock || 0).toLocaleString('pt-BR')}
                                                    </td>
                                                    <td className="px-3 py-2 text-right text-sm text-black dark:text-white">
                                                        {Number(item.cost_price || 0).toLocaleString('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                        })}
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={item.quantity}
                                                            onChange={(event) =>
                                                                updateCartQuantity(item.key, event.target.value)
                                                            }
                                                            className="w-24 rounded-lg border border-mono-300 bg-white px-2 py-1 text-right text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        <Button
                                                            size="sm"
                                                            variant="danger"
                                                            onClick={() => removeCartItem(item.key)}
                                                        >
                                                            Remover
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {/* Dashboard de Graficos */}
                    <SelloutDashboard orders={filteredOrders} period={period} />

                    {/* Tabela de Analise de Produtos */}
                    <ProductAnalysisTable
                        products={processedProducts}
                        loading={false}
                        period={period}
                        onAddToCart={canManagePurchaseOrders ? handleAddToCart : null}
                    />
                </>
            )}
        </div>
    );
};

export default Sellout;
